/**
 * RP-32.2 / 32.3 — a Plan Line's Candidates: the RM's shortlist, anchored to the Who cell.
 *
 * Up to three Candidates per line, each with their NOS hours free over the line's months (the
 * same figure the picker ranks by after Fit), any other open plans naming them, and — on an R1–R4
 * line — their Fit Score. "Make Suggested" promotes one to the line's Suggested Resource
 * and clears the rest (kept as history). A Candidate books no capacity and never reaches
 * the client.
 *
 * Managing needs RM or Admin (`managePlanCandidates`); everyone who can see the plan can
 * read the shortlist. The server says which via `canManage` — this component never decides.
 *
 * Not a modal, for the same reason as the Suggested Resource picker: the row being
 * annotated stays visible beside it.
 */

import { ArrowUpCircle, Plus, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AvailabilitySummary,
  OtherPlansPill,
} from "@/components/plans/ResourceAvailabilitySummary";
import { fitExplanation } from "@/components/plans/SuggestedResourcePicker";
import { ErrorState } from "@/components/shared/error-state";
import { FitBadge } from "@/components/shared/FitBadge";
import {
  usePlanLineCandidates,
  usePromotePlanLineCandidate,
  useRemovePlanLineCandidate,
} from "@/hooks/use-resource-plans";
import { isSplitLine } from "@/lib/plan-line-status";
import { windowLabel } from "@/lib/resource-availability";
import { cn } from "@/lib/utils";
import type { PlanLineCandidateView, PlanLineView } from "@/schemas/resource-plans";

const WIDTH = 380;
const MARGIN = 8;

const OUTCOME_LABELS: Record<string, string> = {
  Removed: "removed",
  Promoted: "made Suggested",
  ClearedOnPromote: "cleared when another was made Suggested",
  ClearedOnStaffed: "cleared when the plan was Staffed",
  LineRemoved: "cleared when the line was removed",
};

interface Props {
  line: PlanLineView;
  anchor: HTMLElement;
  onClose: () => void;
  /** Open the Resource picker in Candidate mode, excluding who is already on the line. */
  onAdd: (excludeIds: ReadonlySet<string>) => void;
}

export function PlanLineCandidatesPopover({ line, anchor, onClose, onAdd }: Props) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [confirming, setConfirming] = useState<PlanLineCandidateView | null>(null);

  const { data, isLoading, isError, error, refetch } = usePlanLineCandidates(line.id, true);
  const remove = useRemovePlanLineCandidate();
  const promote = usePromotePlanLineCandidate();
  const pending = remove.isPending || promote.isPending;

  const reposition = useCallback(() => {
    const box = anchor.getBoundingClientRect();
    const height = panelRef.current?.offsetHeight ?? 320;
    const below = box.bottom + MARGIN;
    const top =
      below + height > window.innerHeight ? Math.max(MARGIN, box.top - height - MARGIN) : below;
    const left = Math.min(
      Math.max(MARGIN, box.left - WIDTH / 2 + box.width / 2),
      window.innerWidth - WIDTH - MARGIN,
    );
    setPos({ top, left });
  }, [anchor]);

  useLayoutEffect(() => {
    reposition();
    const panel = panelRef.current;
    if (panel === null) return;
    const observer = new ResizeObserver(reposition);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [reposition]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (event.target instanceof Node && panelRef.current?.contains(event.target) !== true) {
        onClose();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const id = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const candidates = data?.candidates ?? [];
  const full = data !== undefined && candidates.length >= data.cap;
  const clientRequested = line.suggestedResourceReason === "ClientRequested";
  // RP-24: a split line names no Suggested Resource, so nobody can be made one here.
  const split = isSplitLine(line);

  const doPromote = (c: PlanLineCandidateView) => {
    setConfirming(null);
    promote.mutate({ lineId: line.id, candidateId: c.id }, { onSuccess: onClose });
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Candidates"
      className="fixed z-50 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
      style={{ width: WIDTH, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
    >
      <div className="flex items-start justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            Candidates
            {data !== undefined && (
              <span className="ml-1 font-normal text-muted-foreground">
                {candidates.length} of {data.cap}
              </span>
            )}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {line.laborCategoryName}
            {line.clientLaborCategory !== null && ` · ${line.clientLaborCategory}`}
            {data !== undefined &&
              ` · hours free${data.basis === "Monthly" ? " a month" : ""} over ${windowLabel(data.window)}`}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {data?.lockedReason != null && (
        <p className="border-b border-border bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
          {data.lockedReason}
        </p>
      )}

      <div className="max-h-80 overflow-y-auto">
        {isLoading && <p className="px-3 py-3 text-xs text-muted-foreground">Loading…</p>}
        {isError && (
          <div className="px-3 py-2">
            <ErrorState
              message={error instanceof Error ? error.message : "Could not load the Candidates."}
              onRetry={() => refetch()}
            />
          </div>
        )}
        {data !== undefined && candidates.length === 0 && (
          <p className="px-3 py-3 text-xs text-muted-foreground">
            No Candidates yet.{" "}
            {data.canManage
              ? "Add up to three Candidates to weigh before choosing the Suggested Resource."
              : "The RM adds Candidates here before choosing the Suggested Resource."}
          </p>
        )}
        {candidates.map((c) => (
          <div key={c.id} className="flex flex-col gap-1 border-b border-border px-3 py-2">
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-sm font-medium">{c.name}</span>
              {c.disciplines.map((d) => (
                <span
                  key={d.code}
                  className="rounded bg-background px-1 text-xs font-semibold text-muted-foreground"
                  title={d.name}
                >
                  {d.code}
                </span>
              ))}
              {c.rating !== null && (
                <span className="font-mono text-xs text-muted-foreground">Rating {c.rating}</span>
              )}
              {c.fit !== null && <FitBadge fit={c.fit} size="xs" title={fitExplanation(c.fit)} />}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {c.availability !== null && <AvailabilitySummary availability={c.availability} />}
              <OtherPlansPill plans={c.otherPlans} count={c.otherPlanCount} />
            </div>
            {data?.canManage === true &&
              (split ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    Split line — add them as a Share from the row instead.
                  </span>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => remove.mutate({ lineId: line.id, candidateId: c.id })}
                    className="ml-auto rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    Remove
                  </button>
                </div>
              ) : confirming?.id === c.id ? (
                <div className="flex flex-wrap items-center gap-1.5 rounded-md bg-warning-100 px-2 py-1 text-xs text-warning-950">
                  <span>
                    {line.suggestedResourceName ?? "The current Suggested Resource"} was{" "}
                    <span className="font-semibold">Client Requested</span>. Replace them?
                  </span>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => doPromote(c)}
                    className="rounded border border-border bg-background px-1.5 py-0.5 font-medium"
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(null)}
                    className="px-1 font-medium underline"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => (clientRequested ? setConfirming(c) : doPromote(c))}
                    title="Make them this line's Suggested Resource. The rest of the shortlist is cleared and kept in history. Can move margin if they carry an Internal Rate."
                    className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-xs font-medium transition-colors hover:bg-muted"
                  >
                    <ArrowUpCircle className="h-3 w-3" />
                    Make Suggested
                  </button>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => remove.mutate({ lineId: line.id, candidateId: c.id })}
                    className="rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    Remove
                  </button>
                  {c.addedByName !== null && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      added by {c.addedByName}
                    </span>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>

      {data?.canManage === true && (
        <div className="border-b border-border px-3 py-2">
          <button
            type="button"
            disabled={full || pending}
            onClick={() =>
              onAdd(
                new Set([
                  ...candidates.map((c) => c.resourceId),
                  ...(line.suggestedResourceId === null ? [] : [line.suggestedResourceId]),
                ]),
              )
            }
            title={full ? `A line holds at most ${data.cap} Candidates.` : undefined}
            className={cn(
              "inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium transition-colors hover:bg-muted",
              full && "opacity-60",
            )}
          >
            <Plus className="h-3 w-3" />
            {full ? `${data.cap} Candidates — the most a line holds` : "Add Candidate"}
          </button>
        </div>
      )}

      {data !== undefined && data.history.length > 0 && (
        <details className="px-3 py-1.5 text-xs text-muted-foreground">
          <summary className="cursor-pointer">History ({data.history.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {data.history.map((h) => (
              <li key={h.id}>
                {h.name} — {OUTCOME_LABELS[h.outcome] ?? h.outcome}
                {h.clearedByName !== null && ` by ${h.clearedByName}`},{" "}
                {new Date(h.clearedAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
        </details>
      )}

      <p className="rounded-b-lg border-t border-border bg-muted/30 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
        A Candidate books no capacity and is never shown to the client. Only the{" "}
        <span className="font-medium">Suggested Resource</span> goes forward to Staffed.
      </p>
    </div>
  );
}
